import { PlatformCatalogListItemDTO } from "@bvhi/backend-api";
import { GridColDef } from "@bvhi/ui-base";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionCells } from "../../../../../../components/datagrid/cells/action-cells";
import { DateCell } from "../../../../../../components/datagrid/cells/date-cell";
import { PlatformCatalogAccessTypeChip } from "../../components/platform-catalog-access-type-chip";
import { PlatformCatalogSynchronisationStatusChip } from "../../components/platform-catalog-synchronsation-status-chip";
import { CatalogsDetailLinkCell } from "../components/datagrid/catalogs-detail-link-cell";
import { PlatformCatalogStatusChip } from "../components/platform-catalog-status-chip";
import { useCatalogsSortParams } from "./use-catalogs-sort-params";

export const useCatalogsDataGridDefinitions = (handleDelete: (userId: string) => void) => {
  const { t } = useTranslation(["catalogs"]);
  const { isFieldSortable } = useCatalogsSortParams();

  const columns: GridColDef<PlatformCatalogListItemDTO>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: t("catalogs.datagrid.header.name"),
        minWidth: 150,
        width: 250,
        maxWidth: 300,
        sortable: isFieldSortable("name"),
        renderCell: (params) => <CatalogsDetailLinkCell catalogId={params.row.id} label={params.row.name} />,
      },
      {
        field: "platformCatalogStatus",
        headerName: t("catalogs:catalogs.datagrid.header.platform.catalog.status"),
        minWidth: 200,
        width: 250,
        maxWidth: 350,
        sortable: isFieldSortable("platformCatalogStatus"),
        renderCell: (params) => <PlatformCatalogStatusChip catalog={params.row} />,
      },
      {
        field: "platformCatalogAccessType",
        headerName: t("catalogs:catalogs.datagrid.header.platform.catalog.access.type"),
        minWidth: 200,
        width: 300,
        maxWidth: 500,
        sortable: isFieldSortable("platformCatalogAccessType"),
        renderCell: (params) => <PlatformCatalogAccessTypeChip status={params.row.platformCatalogAccessType} />,
      },
      {
        field: "platformCatalogSynchronisationStatus",
        headerName: t("catalogs:catalogs.datagrid.header.platform.catalog.synchronisation.status"),
        minWidth: 200,
        width: 250,
        maxWidth: 300,
        sortable: isFieldSortable("platformCatalogSynchronisationStatus"),
        renderCell: (params) => (
          <PlatformCatalogSynchronisationStatusChip
            platformCatalogSynchronisationStatus={params.row.platformCatalogSynchronisationStatus}
          />
        ),
      },
      {
        field: "versionCount",
        headerName: t("catalogs.datagrid.header.version.count"),
        minWidth: 100,
        width: 100,
        maxWidth: 150,
        sortable: isFieldSortable("versionCount"),
      },
      {
        field: "activeVersion",
        headerName: t("catalogs.datagrid.header.active.version"),
        minWidth: 100,
        width: 125,
        maxWidth: 150,
        valueGetter: (_value, row) => `${row.activeVersion?.version ?? t("catalogs:catalogs.datagrid.no.active.version")}`,
        sortable: isFieldSortable("activeVersion"),
      },
      {
        field: "createdAt",
        headerName: t("catalogs.datagrid.header.created.at"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("createdAt"),
        renderCell: (params) => <DateCell date={params.row.createdAt} />,
      },
      {
        field: "updatedAt",
        headerName: t("catalogs.datagrid.header.updated.at"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("updatedAt"),
        renderCell: (params) => <DateCell date={params.row.updatedAt} />,
      },
      {
        field: "id",
        headerName: t("catalogs:catalogs.datagrid.header.actions"),
        flex: 1,
        minWidth: 125,
        headerClassName: "datagrid-sticky-right",
        cellClassName: "datagrid-sticky-right",
        align: "right",
        headerAlign: "right",
        sortable: false,
        resizable: false,
        renderCell: (params) => (
          <ActionCells
            deleteActionDisabled={params.row.versionCount > 0}
            deletePermission="catalog:delete"
            detailParams={{ catalogId: params.row.id }}
            detailPermission="catalog:read"
            detailRoute="/app/catalogs/catalog/$catalogId"
            handleDelete={handleDelete}
            id={params.row.id}
            showDeleteAction={true}
            showDetailAction={true}
          />
        ),
      },
    ];
  }, [t, isFieldSortable, handleDelete]);

  return { columns };
};
